import { ref } from "vue";
import type { Ref } from 'vue';
import type { UploadFile } from '@/types'; // adjust if your UploadFile type is elsewhere
import { processFileUpload, validateFile } from "@/composables/useFileUpload";

/**
 * Composable for document upload handling.
 * @param store - An object implementing upload/delete methods for passport, license, and vhf license.
 */
export function useDocumentUpload(store: {
  uploadPassportDocument: (file: File) => Promise<any>,
  uploadLicenseDocument: (file: File) => Promise<any>,
  uploadVHFLicenseDocument: (file: File) => Promise<any>,
  deletePassportDocument: (uuid: string) => Promise<any>,
  deleteLicenseDocument: (uuid: string) => Promise<any>,
  deleteVHFLicenseDocument: (uuid: string) => Promise<any>
}) {
  const licenseUploadError = ref('');
  const passportUploadError = ref('');
  const vhfLicenseUploadError = ref('');
  const uploadedLicense: Ref<UploadFile[]> = ref([]);
  const uploadedPassport: Ref<UploadFile[]> = ref([]);
  const uploadedVHFLicense: Ref<UploadFile[]> = ref([]);

  function handleFileInput(event: Event, type: 'passport' | 'license' | 'vhf', listId?: string, crewId?: string) {
    const input = event.target as HTMLInputElement;
    const inputFiles = Array.from(input.files || []);
    const errorRef = getErrorRef(type);
    errorRef.value = '';
    inputFiles.forEach((file) => validateAndProcessFile(file, type, listId, crewId));
    input.value = '';
  }

  function handleFileDrop(event: DragEvent, type: 'passport' | 'license' | 'vhf', listId?: string, crewId?: string) {
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    const errorRef = getErrorRef(type);
    errorRef.value = '';
    droppedFiles.forEach((file) => validateAndProcessFile(file, type, listId, crewId));
  }

  function validateAndProcessFile(
      file: File,
      type: 'passport' | 'license' | 'vhfLicense',
      listId?: string,
      crewId?: string
  ) {
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSizeMB = 2;
    const errorRef = getErrorRef(type);
    if (!validateFile(file, errorRef, allowedTypes, maxSizeMB)) return;

    const targetRef = getTargetRef(type);
    const baseUploadFn = getUploadFn(type);

    const uploadFn = (file: File) => {
      if (listId && crewId) {
        return (baseUploadFn as any)(listId, crewId, file);
      }
      return baseUploadFn(file);
    };

    processFileUpload(file, uploadFn, targetRef, {
      onError: () => {
        errorRef.value = `${type.toUpperCase()} upload failed.`;
      }
    });
  }

  async function removeUploadedFile(uuid: string, type: 'passport' | 'license' | 'vhf') {
    try {
      const deleteFn = getDeleteFn(type);
      const listRef = getTargetRef(type);
      await deleteFn(uuid);
      listRef.value = listRef.value.filter((file: any) => file.uuid !== uuid);
    } catch (error) {
      console.error(`Failed to delete ${type} file:`, error);
    }
  }

  function getErrorRef(type: 'passport' | 'license' | 'vhfLicense') {
    return type === 'passport'
      ? passportUploadError
      : type === 'license'
      ? licenseUploadError
      : vhfLicenseUploadError;
  }

  function getTargetRef(type: 'passport' | 'license' | 'vhfLicense') {
    return type === 'passport'
      ? uploadedPassport
      : type === 'license'
      ? uploadedLicense
      : uploadedVHFLicense;
  }

  function getUploadFn(type: 'passport' | 'license' | 'vhfLicense') {
    return type === 'passport'
      ? store.uploadPassportDocument
      : type === 'license'
      ? store.uploadLicenseDocument
      : store.uploadVHFLicenseDocument;
  }

  function getDeleteFn(type: 'passport' | 'license' | 'vhfLicense') {
    return type === 'passport'
      ? store.deletePassportDocument
      : type === 'license'
      ? store.deleteLicenseDocument
      : store.deleteVHFLicenseDocument;
  }

  return {
    uploadedLicense,
    uploadedPassport,
    uploadedVHFLicense,
    licenseUploadError,
    passportUploadError,
    vhfLicenseUploadError,
    handleFileInput,
    handleFileDrop,
    validateAndProcessFile,
    removeUploadedFile
  };
}